import { ReactNode } from 'react'
import { InfiniteScrollList } from 'components/containers/infinite-scroll-list'
import { SearchField } from 'components/form/search-field'
import { NavItemSkeleton } from '../../components/NavItem'
import { useTranslation } from 'react-i18next'
import { IconButton, MenuButton } from 'components/buttons'
import { faClose, faSearch } from '@fortawesome/pro-solid-svg-icons'
import { ColumnHeading } from './ColumnHeading'
import { faFilter } from '@fortawesome/pro-regular-svg-icons'
import { FilterMenuItem } from './FilterMenuItem'
import { ActiveFilter } from './types'
import { noop } from '@utils/noop'

type Props<T> = {
  children?: ReactNode
  displaySearch?: boolean
  filter?: ActiveFilter
  heading?: string
  loadMoreItems: () => void
  isFetching: boolean
  isInitiallyLoading: boolean
  items: T[]
  activeFilterEnabled: boolean
  onActiveFilterChange?: (filter: ActiveFilter) => void
  onSearchChange: (search: string) => void
  onSearchToggle: (displayed: boolean) => void
  placeholder?: string
  renderItem: (item: T) => ReactNode
  skeletonItem?: ReactNode
  subheading?: string
  searchError?: string
  searchValue: string
  totalItems?: number
}

export const Column = <T,>({
  children,
  filter,
  heading,
  isFetching,
  isInitiallyLoading,
  items,
  loadMoreItems,
  onActiveFilterChange = noop,
  activeFilterEnabled = false,
  onSearchToggle,
  renderItem,
  skeletonItem = <NavItemSkeleton />,
  subheading,
  totalItems,
  displaySearch,
  ...props
}: Props<T>) => {
  const { t } = useTranslation()

  return (
    <>
      <div className="mb-2 mx-9 flex items-center text-neutral-700">
        <ColumnHeading
          className="mr-auto"
          heading={heading}
          subheading={subheading}
        />
        <IconButton
          icon={displaySearch ? faClose : faSearch}
          onClick={() => onSearchToggle(!displaySearch)}
          iconProps={{ size: 'sm' }}
          srOnlyLabel={displaySearch ? 'Close Search' : 'Search'}
        />
        <MenuButton
          button={({ disabled, toggleMenuHidden }) => (
            <IconButton
              annotate={
                activeFilterEnabled &&
                // A filter is being applied if either active or inactive are set,
                // but not both or neither
                (filter?.active ?? false) !== (filter?.inactive ?? false)
              }
              disabled={disabled || !activeFilterEnabled}
              icon={faFilter}
              onClick={toggleMenuHidden}
            />
          )}
          closeOnItemClick={false}
          menuItems={[
            {
              onClick: () =>
                onActiveFilterChange({ ...filter, active: !filter?.active }),
              content: (
                <FilterMenuItem
                  variant="active"
                  checked={Boolean(filter?.active)}
                />
              ),
            },
            {
              onClick: () =>
                onActiveFilterChange({
                  ...filter,
                  inactive: !filter?.inactive,
                }),
              content: (
                <FilterMenuItem
                  variant="inactive"
                  checked={Boolean(filter?.inactive)}
                />
              ),
            },
          ]}
        />
      </div>

      {displaySearch && (
        <SearchField
          autoFocus={true}
          error={props.searchError}
          onChange={props.onSearchChange}
          placeholder={props.placeholder ?? t('common.search').toString()}
          value={props.searchValue}
          className="mx-9 mb-4"
        />
      )}
      {children && (
        <div className="mb-1 flex flex-col items-center">{children}</div>
      )}
      <InfiniteScrollList
        items={items}
        loadMore={loadMoreItems}
        isFetching={isFetching}
        isInitiallyLoading={isInitiallyLoading}
        renderItem={renderItem}
        renderNoItems={() => (
          <div className="flex flex-row items-center h-10 text-sm italic font-medium text-neutral-500 mx-9">
            {t('features.taskPicker.noItems')}
          </div>
        )}
        expectedItemHeight={40}
        totalItems={totalItems ?? 0}
        skeletonItem={skeletonItem}
      />
    </>
  )
}
